import { z } from 'zod';
import { tbmSignaturePdfsSchema } from './signature-pdf.js';
import {
  actorSnapshotSchema,
  entityIdSchema,
  nonEmptyTextSchema,
  resourceScopeSchema,
  sha256Schema,
  utcDateTimeSchema,
} from '@kop/contracts';

export const tbmStages = [
  'draft',
  'submitted',
  'under-review',
  'returned',
  'resubmitted',
  'recorded',
  'approved',
  'closed',
] as const;
export const tbmStageSchema = z.enum(tbmStages);

export const pcProcessCodes = [
  'rebar-assembly',
  'mold-setup',
  'concrete-pouring',
  'curing',
  'demolding',
  'lifting-crane',
  'yard-stacking',
  'shipping',
  'equipment-maintenance',
  'non-routine',
] as const;
export const pcProcessCodeSchema = z.enum(pcProcessCodes);

export const recommendationSourceSchema = z.enum([
  'planned-work',
  'equipment',
  'area',
  'weather',
  'previous-finding',
  'company-standard',
]);

export const dailyTbmHazardCodes = [
  'fall',
  'caught-in',
  'heavy-load',
  'electric-shock',
  'trip',
  'fire',
  'falling-object',
  'collision',
  'harmful-substance',
  'cut',
  'other',
] as const;
export const dailyTbmHazardCodeSchema = z.enum(dailyTbmHazardCodes);

/** Independent resident-contractor daily TBM. Future documents are optional references only. */
export const dailyTbmSummarySchema = z
  .object({
    businessDate: z.iso.date(),
    contractorName: nonEmptyTextSchema.max(300),
    workCategoryId: entityIdSchema.optional(),
    workCategoryLabel: nonEmptyTextSchema.max(300),
    workLocations: z
      .array(
        z
          .object({
            workLocationId: entityIdSchema,
            label: nonEmptyTextSchema.max(300),
          })
          .strict(),
      )
      .max(50)
      .optional(),
    turnoutCount: z.number().int().nonnegative(),
    attendanceCount: z.number().int().positive(),
    mainWorks: z.array(nonEmptyTextSchema.max(500)).min(1).max(4),
    hazardCodes: z.array(dailyTbmHazardCodeSchema).min(1).max(dailyTbmHazardCodes.length),
    communications: z.array(nonEmptyTextSchema.max(1_000)).max(4),
    facilitatorName: nonEmptyTextSchema.max(200),
    facilitatorJobTitle: z.string().trim().max(200).nullable(),
    optionalReferences: z
      .array(
        z
          .object({
            kind: z.enum(['risk-assessment', 'work-plan', 'standard-work-procedure']),
            artifactId: entityIdSchema,
            revision: z.number().int().positive(),
            contentHash: sha256Schema,
          })
          .strict(),
      )
      .max(10),
    legacyAttendanceProof: z
      .enum(['current-headcount', 'current-named-roster', 'legacy-count-only'])
      .default('current-headcount'),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.attendanceCount > value.turnoutCount && value.turnoutCount > 0) {
      context.addIssue({
        code: 'custom',
        path: ['attendanceCount'],
        message: 'TBM attendance cannot exceed the contractor turnout',
      });
    }
  });

export const riskRecommendationSchema = z
  .object({
    recommendationId: entityIdSchema,
    source: recommendationSourceSchema,
    sourceId: entityIdSchema.nullable(),
    process: pcProcessCodeSchema,
    hazard: nonEmptyTextSchema.max(300),
    countermeasure: nonEmptyTextSchema.max(500),
    priority: z.number().int().min(1).max(100),
    rationale: nonEmptyTextSchema.max(500),
  })
  .strict();

export const workItemSchema = z
  .object({
    workItemId: entityIdSchema,
    process: pcProcessCodeSchema,
    description: nonEmptyTextSchema.max(300),
    areaIds: z.array(entityIdSchema).min(1),
    equipmentIds: z.array(entityIdSchema),
  })
  .strict();

export const coreRiskSchema = z
  .object({
    riskId: entityIdSchema,
    process: pcProcessCodeSchema,
    hazard: nonEmptyTextSchema.max(300),
    countermeasure: nonEmptyTextSchema.max(500),
    recommendationId: entityIdSchema.nullable(),
    communicated: z.boolean(),
    controls: z
      .array(
        z
          .object({
            controlId: entityIdSchema,
            action: nonEmptyTextSchema.max(500),
            status: z.enum(['confirmed', 'deficient', 'not-applicable']),
            exceptionDisposition: z
              .enum(['resolved-before-work', 'work-stopped', 'awaiting-action'])
              .nullable(),
          })
          .strict(),
      )
      .min(1)
      .max(3),
  })
  .strict()
  .superRefine((value, context) => {
    value.controls.forEach((control, index) => {
      if (control.status === 'deficient' && control.exceptionDisposition === null) {
        context.addIssue({
          code: 'custom',
          path: ['controls', index, 'exceptionDisposition'],
          message: 'A deficient control requires resolution or work-stop disposition',
        });
      }
      if (control.status !== 'deficient' && control.exceptionDisposition !== null) {
        context.addIssue({
          code: 'custom',
          path: ['controls', index, 'exceptionDisposition'],
          message: 'Only deficient controls carry an exception disposition',
        });
      }
    });
  });

export const captureMetadataSchema = z
  .object({
    capturedAt: utcDateTimeSchema.nullable(),
    orientation: z.number().int().min(1).max(8),
    sourceMimeType: nonEmptyTextSchema.max(100),
    sourceBytes: z.number().int().nonnegative(),
    sourceWidth: z.number().int().positive().nullable(),
    sourceHeight: z.number().int().positive().nullable(),
    hadGps: z.boolean(),
    userDeclaredLocation: z.string().trim().max(300).nullable(),
  })
  .strict();

export const tbmPhotoEvidenceSchema = z
  .object({
    evidenceId: entityIdSchema,
    uploadManifestId: entityIdSchema,
    sourceSha256: sha256Schema,
    thumbnailVariantSha256: sha256Schema.optional(),
    evidenceVariantSha256: sha256Schema,
    capture: captureMetadataSchema,
    watermark: z
      .object({
        siteLabel: nonEmptyTextSchema.max(200),
        occurredAt: utcDateTimeSchema,
        rendered: z.literal(true),
      })
      .strict(),
    serverCommitted: z.boolean(),
    immutable: z.boolean(),
  })
  .strict();

/** Exact, immutable approved RA snapshot pinned into a resident-contractor TBM. */
export const routineRiskSourceSnapshotSchema = z
  .object({
    assessmentId: entityIdSchema,
    revision: z.number().int().positive(),
    contentHash: sha256Schema,
    approvedAt: utcDateTimeSchema,
    siteName: nonEmptyTextSchema.max(200),
    contractorName: nonEmptyTextSchema.max(300),
    workArea: nonEmptyTextSchema.max(500),
    workSummary: nonEmptyTextSchema.max(500),
    hazards: z
      .array(
        z
          .object({
            sourceRiskId: entityIdSchema,
            hazard: nonEmptyTextSchema.max(2_000),
            controls: z.array(nonEmptyTextSchema.max(2_000)).min(1),
          })
          .strict(),
      )
      .min(1),
  })
  .strict();

export const understandingCheckSchema = z
  .object({
    method: z.enum(['group-response', 'sample-repeat-back', 'question-and-answer', 'mixed']),
    participantCount: z.number().int().positive(),
    understoodCount: z.number().int().nonnegative(),
    sampleQuestion: z.string().trim().max(500).nullable(),
    sampleAnswerSummary: z.string().trim().max(1_000).nullable(),
    needsFollowUpCount: z.number().int().nonnegative(),
    checkedAt: utcDateTimeSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.understoodCount + value.needsFollowUpCount > value.participantCount) {
      context.addIssue({
        code: 'custom',
        path: ['understoodCount'],
        message: 'Understanding totals exceed participants',
      });
    }
    if (
      value.method === 'sample-repeat-back' &&
      (!value.sampleQuestion || !value.sampleAnswerSummary)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['sampleQuestion'],
        message: 'Repeat-back requires question and answer summary',
      });
    }
  });

export const attendanceEvidenceSchema = z
  .object({
    mode: z.enum(['headcount-by-area', 'named-roster', 'mixed']),
    headcountByArea: z.record(entityIdSchema, z.number().int().nonnegative()),
    totalCount: z.number().int().positive(),
    rosterIds: z.array(entityIdSchema),
    facilitatorSignatureEvidenceId: entityIdSchema.nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    const sum = Object.values(value.headcountByArea).reduce((total, count) => total + count, 0);
    if (sum !== value.totalCount) {
      context.addIssue({
        code: 'custom',
        path: ['totalCount'],
        message: 'Area headcount must equal total attendance',
      });
    }
    if (value.mode === 'named-roster' && value.rosterIds.length !== value.totalCount) {
      context.addIssue({
        code: 'custom',
        path: ['rosterIds'],
        message: 'Named roster must cover attendance',
      });
    }
  });

export const tbmPlanSchema = z
  .object({
    planId: entityIdSchema,
    plannedDate: z.iso.date(),
    areaIds: z.array(entityIdSchema).min(1),
    contractorOrganizationId: entityIdSchema,
    workItems: z.array(workItemSchema).min(1),
    recommendations: z.array(riskRecommendationSchema),
    managerMessage: z.string().trim().max(2_000).nullable(),
    generatedAt: utcDateTimeSchema,
  })
  .strict();

export const tbmExecutionSchema = z
  .object({
    startedAt: utcDateTimeSchema,
    endedAt: utcDateTimeSchema,
    facilitator: actorSnapshotSchema,
    areaIds: z.array(entityIdSchema).min(1),
    workItems: z.array(workItemSchema).min(1),
    coreRisks: z.array(coreRiskSchema).min(1).max(dailyTbmHazardCodes.length),
    announcements: z.string().trim().max(2_000).nullable(),
    healthStatusChecked: z.boolean(),
    ppeStatusChecked: z.boolean(),
    attendance: attendanceEvidenceSchema,
    understanding: understandingCheckSchema,
    questionChannelExplained: z.boolean(),
    stopWorkSignalExplained: z.boolean(),
    questionsAndResponses: z.array(
      z
        .object({ question: nonEmptyTextSchema.max(500), response: nonEmptyTextSchema.max(1_000) })
        .strict(),
    ),
    photos: z.array(tbmPhotoEvidenceSchema).min(1).max(6),
  })
  .strict()
  .superRefine((value, context) => {
    if (Date.parse(value.endedAt) <= Date.parse(value.startedAt)) {
      context.addIssue({
        code: 'custom',
        path: ['endedAt'],
        message: 'TBM must end after it starts',
      });
    }
    if (value.coreRisks.some((risk) => !risk.communicated)) {
      context.addIssue({
        code: 'custom',
        path: ['coreRisks'],
        message: 'Every core risk and control must be communicated',
      });
    }
    if (value.understanding.participantCount !== value.attendance.totalCount) {
      context.addIssue({
        code: 'custom',
        path: ['understanding'],
        message: 'Understanding check must cover the attendance scope',
      });
    }
  });

/** Count-and-photo execution record. It deliberately contains no implied conduct checks. */
export const tbmAttendancePhotoRecordInputSchema = z
  .object({
    kind: z.literal('attendance-photo-record'),
    conductedAt: utcDateTimeSchema,
    participantCount: z.number().int().positive(),
    photos: z.array(tbmPhotoEvidenceSchema).min(1).max(6),
  })
  .strict();
export const tbmAttendancePhotoRecordSchema = tbmAttendancePhotoRecordInputSchema.extend({
  facilitator: actorSnapshotSchema,
  organizationId: entityIdSchema,
  plannedDate: z.iso.date(),
  planId: entityIdSchema,
  routineRiskReference: z
    .object({ assessmentId: entityIdSchema, revision: z.number().int().positive(), contentHash: sha256Schema })
    .strict()
    .nullable(),
  uncollected: z.object({
    understanding: z.literal('not-collected'), healthStatus: z.literal('not-collected'),
    ppeStatus: z.literal('not-collected'), questionChannel: z.literal('not-collected'),
    stopWorkSignal: z.literal('not-collected'),
  }).strict(),
}).strict();

export const tbmRecordSchema = z
  .object({
    recordId: entityIdSchema,
    documentVersionId: entityIdSchema,
    route: z.enum(['routine-partner-tbm', 'work-control-case-tbm']).optional(),
    workControlLink: z
      .object({
        caseId: entityIdSchema,
        caseRevision: z.number().int().positive(),
        contentHash: sha256Schema,
      })
      .strict()
      .nullable()
      .optional(),
    routineRiskSource: routineRiskSourceSnapshotSchema.nullable().optional(),
    dailySummary: dailyTbmSummarySchema.nullable().optional(),
    scope: resourceScopeSchema.refine(
      (scope) => scope.kind === 'contractor',
      'TBM must be contractor scoped',
    ),
    version: z.number().int().positive(),
    contentHash: sha256Schema.optional(),
    stage: tbmStageSchema,
    plan: tbmPlanSchema,
    execution: tbmExecutionSchema.nullable(),
    attendancePhotoRecord: tbmAttendancePhotoRecordSchema.nullable().optional(),
    signaturePdfs: tbmSignaturePdfsSchema.optional(),
    submittedAt: utcDateTimeSchema.nullable(),
    serverSubmittedAt: utcDateTimeSchema.nullable(),
    reviews: z.array(
      z
        .object({
          decision: z.enum(['returned', 'approved']),
          opinion: nonEmptyTextSchema.max(2_000),
          reviewer: actorSnapshotSchema,
          reviewedAt: utcDateTimeSchema,
        })
        .strict(),
    ),
    lock: z
      .object({ contentHash: sha256Schema, lockedAt: utcDateTimeSchema, lockedBy: entityIdSchema })
      .strict()
      .nullable(),
    createdBy: entityIdSchema.optional(),
    submittedBy: entityIdSchema.optional(),
    createdAt: utcDateTimeSchema,
    updatedAt: utcDateTimeSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (
      value.plan.contractorOrganizationId !==
      (value.scope.kind === 'contractor' ? value.scope.contractorId : '')
    ) {
      context.addIssue({
        code: 'custom',
        path: ['plan', 'contractorOrganizationId'],
        message: 'Plan contractor must match record scope',
      });
    }
    if (
      ['submitted', 'under-review', 'resubmitted', 'recorded', 'approved', 'closed'].includes(value.stage) &&
      !value.execution && !value.attendancePhotoRecord
    ) {
      context.addIssue({
        code: 'custom',
        path: ['execution'],
        message: 'Submitted TBM requires execution evidence',
      });
    }
    if (['recorded', 'approved', 'closed'].includes(value.stage) !== (value.lock !== null)) {
      context.addIssue({
        code: 'custom',
        path: ['lock'],
        message: 'Approved and closed TBM records must be integrity locked',
      });
    }
  });

export type TbmStage = z.infer<typeof tbmStageSchema>;
export type PcProcessCode = z.infer<typeof pcProcessCodeSchema>;
export type RiskRecommendation = z.infer<typeof riskRecommendationSchema>;
export type TbmPlan = z.infer<typeof tbmPlanSchema>;
export type TbmExecution = z.infer<typeof tbmExecutionSchema>;
export type TbmAttendancePhotoRecord = z.infer<typeof tbmAttendancePhotoRecordSchema>;
export type TbmRecord = z.infer<typeof tbmRecordSchema>;
export type RoutineRiskSourceSnapshot = z.infer<typeof routineRiskSourceSnapshotSchema>;
export type DailyTbmSummary = z.infer<typeof dailyTbmSummarySchema>;
export type DailyTbmHazardCode = z.infer<typeof dailyTbmHazardCodeSchema>;
